// ⚠️  TEAMS SDK HELPERS — READ _docs/TEAMS_LINK_HANDLING.md BEFORE EDITING ⚠️
//
// Key constraint: after calling window.open() in Teams context, do NOT fall back to
// app.openLink() when the return value is null.  Teams Desktop WebView2 returns null
// even when it successfully routed the URL to the system browser.  Falling back causes
// the URL to open twice (two browser tabs).  See openExternalLink() below.
import { app, authentication } from '@microsoft/teams-js';
import { isTeamsEmbedded, normalizeOpenableUrl } from './teamsHost';

import { recordTeamsLinkAttempt } from './teamsLinkDebug';

let teamsInitPromise: Promise<void> | null = null;
let teamsSdkReady = false;
let teamsSdkInitFailed = false; // true when SDK confirmed "no parent window" — not in Teams
let teamsHostClientType: string | undefined;

/** True once the npm Teams SDK finished app.initialize(). */
export function isTeamsSdkReady(): boolean {
  return teamsSdkReady || app.isInitialized();
}

/** Use Teams host APIs instead of a normal browser tab. */
export function shouldOpenInTeamsHost(): boolean {
  return isTeamsEmbedded(); // SDK-ready alone is not sufficient; require confirmed host context
}

export function setTeamsHostClientType(clientType: string | undefined): void {
  teamsHostClientType = clientType;
}

export function getTeamsHostClientType(): string | undefined {
  return teamsHostClientType;
}

/** Initialize Teams SDK once for the host tab or auth popup. */
export async function ensureTeamsInitialized(): Promise<void> {
  if (teamsSdkReady) return;
  if (teamsInitPromise) return teamsInitPromise;

  teamsInitPromise = (async () => {
    try {
      if (!app.isInitialized()) {
        await app.initialize();
      }
      teamsSdkReady = true;
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      // "already" = double-init attempt; SDK was already set up for a real Teams frame.
      if (/already/i.test(message)) {
        teamsSdkReady = true;
        return;
      }
      // "No Parent window found" = genuinely not running inside a Teams frame.
      // This is expected when testing with ?teams=true in a plain browser.
      // Mark the failure so openExternalLink falls back to window.open().
      if (/no parent window/i.test(message)) {
        teamsInitPromise = null;
        teamsSdkInitFailed = true;
        console.warn('[Teams SDK] Initialization skipped: no Teams parent window found.');
        return;
      }
      teamsInitPromise = null;
      throw error;
    }
  })();

  return teamsInitPromise;
}


export async function getTeamsAuthenticationModule(timeoutMs = 15000) {
  const authModulePromise = (async () => {
    await ensureTeamsInitialized();
    if (!authentication?.authenticate) {
      throw new Error('Teams authentication API is not available');
    }
    return authentication;
  })();

  let timeoutId: number | undefined;
  const timeout = new Promise<never>((_, reject) => {
    timeoutId = window.setTimeout(
      () => reject(new Error('Teams authentication API timed out')),
      timeoutMs,
    );
  });

  try {
    return await Promise.race([authModulePromise, timeout]);
  } finally {
    if (timeoutId) window.clearTimeout(timeoutId);
  }
}

export async function notifyTeamsAuthSuccess(result: string): Promise<void> {
  await ensureTeamsInitialized();
  await authentication.notifySuccess(result);
}

export async function notifyTeamsAuthFailure(message: string): Promise<void> {
  await ensureTeamsInitialized();
  await authentication.notifyFailure(message);
}

export function copyExternalLink(url: string): void {
  const normalized = normalizeOpenableUrl(url) ?? url.trim();
  if (!normalized) return;

  if (navigator.clipboard?.writeText) {
    void navigator.clipboard.writeText(normalized);
    return;
  }
  window.prompt('Copy this link:', normalized);
}

// Deduplication: track the last URL + timestamp to suppress double-fires that occur when the
// global capture listener fires on pointerdown AND the browser also fires a synthetic click
// event (Teams Desktop WebView2 does not honour event.preventDefault() on pointerdown for click).
let _lastOpenUrl = '';
let _lastOpenAt = 0;

/** Open a URL in the system browser (Teams) or a new tab (standalone). */
export function openExternalLink(url: string, _label?: string): void {
  const normalized = normalizeOpenableUrl(url);
  recordTeamsLinkAttempt(url, 'openExternalLink called');

  // Deduplicate: same URL opened within 800 ms → skip (double-fire guard).
  const now = Date.now();
  const dedupeKey = normalized ?? url;
  if (dedupeKey === _lastOpenUrl && now - _lastOpenAt < 800) {
    recordTeamsLinkAttempt(url, 'openExternalLink skipped (double-fire dedupe)');
    return;
  }
  _lastOpenUrl = dedupeKey;
  _lastOpenAt = now;
  if (!normalized) {
    recordTeamsLinkAttempt(url, 'rejected: not openable http(s) url');
    return;
  }

  // Not in Teams, or SDK confirmed no Teams parent window — plain browser behaviour.
  if (!isTeamsEmbedded() || teamsSdkInitFailed) {
    recordTeamsLinkAttempt(normalized, teamsSdkInitFailed ? 'browser window.open (sdk init failed)' : 'browser window.open');
    window.open(normalized, '_blank', 'noopener,noreferrer');
    return;
  }

  // Teams Modern Desktop (WebView2) and Teams Web both open URLs via window.open() from the
  // tab-app iframe.  Critically, Teams Desktop's WebView2 host intercepts window.open() and
  // launches the URL in a new Edge / system-browser window — but returns null to JavaScript
  // (the same as a "blocked popup" from JS's perspective).  If we then also call app.openLink()
  // the URL opens a second time.  So: call window.open(), and if it returns null treat the URL
  // as already handled by the Teams host (do NOT fall back to app.openLink()).
  recordTeamsLinkAttempt(normalized, `teams window.open (clientType=${teamsHostClientType ?? 'unknown'})`);
  const opened = window.open(normalized, '_blank', 'noopener,noreferrer');
  if (!opened) {
    // Teams Desktop WebView2 returns null even when it successfully routed the URL to the
    // system browser.  Log it and stop — do NOT call app.openLink() or we double-open.
    recordTeamsLinkAttempt(normalized, 'window.open returned null (handled by Teams host)');
  }
}
